// src/pages/schedule/FieldsPage.jsx
// Manage a team's saved field/venue library.
// Fields stored at: teams/{teamId}/fields/{fieldId}

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection, addDoc, updateDoc, deleteDoc,
  doc, onSnapshot, query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import toast from 'react-hot-toast';
import clsx from 'clsx';

import { Text } from '@/components/text';
import GooglePlacesInput from '@/components/GooglePlacesInput';
import TeamSubNav from '@/components/team/TeamSubNav';

import {
  MapPinIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  BoltSlashIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */
export const FIELD_TYPES = [
  { value: 'grass',  label: '🌿 Grass',  isIndoor: false },
  { value: 'turf',   label: '🟩 Turf',   isIndoor: false },
  { value: 'indoor', label: '🏟️ Indoor', isIndoor: true  },
  { value: 'other',  label: '📍 Other',  isIndoor: false },
];

const EMPTY_FORM = {
  name:      '',
  address:   '',
  lat:       null,
  lng:       null,
  placeId:   null,
  fieldType: 'grass',
  isIndoor:  false,
  notes:     '',
};

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */
function fieldEmoji(field) {
  if (field.isIndoor)              return '🏟️';
  if (field.fieldType === 'turf')  return '🟩';
  if (field.fieldType === 'grass') return '🌿';
  return '📍';
}

/* ------------------------------------------------------------------ */
/* Field form (add / edit)                                              */
/* ------------------------------------------------------------------ */
function FieldForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handlePlaceSelect = useCallback((place) => {
    if (!place) {
      set('address', ''); set('lat', null); set('lng', null); set('placeId', null);
      return;
    }
    setForm(f => ({
      ...f,
      address: place.address,
      lat:     place.lat,
      lng:     place.lng,
      placeId: place.placeId,
      // Auto-fill name only if still empty
      name: f.name || place.name,
    }));
  }, []);

  const handleTypeChange = (val) => {
    const t = FIELD_TYPES.find(f => f.value === val);
    setForm(f => ({ ...f, fieldType: val, isIndoor: t?.isIndoor ?? false }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim())        { toast.error('Field name is required.'); return; }
    if (!form.lat || !form.lng)   { toast.error('Select a location from the search results.'); return; }
    onSave(form);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mb-6 rounded-2xl border border-zinc-200 bg-white p-5 space-y-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]"
    >
      <h2
        className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
        style={{ fontFamily: HEAD_FONT }}
      >
        {initial ? 'Edit Field' : 'Add Field'}
      </h2>

      {/* Name */}
      <div>
        <label
          className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1.5"
          style={{ fontFamily: HEAD_FONT }}
        >
          Field Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={form.name}
          onChange={e => set('name', e.target.value)}
          placeholder="e.g. Riverside Park — Field 1"
          required
          className="block w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
        />
      </div>

      {/* Location via Google Places */}
      <div>
        <label
          className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1.5"
          style={{ fontFamily: HEAD_FONT }}
        >
          Location <span className="text-red-500">*</span>
        </label>
        <GooglePlacesInput
          onPlace={handlePlaceSelect}
          defaultValue={form.address}
          placeholder="Search for a park, school, sports complex…"
        />
        {form.lat && (
          <p className="mt-1.5 flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
            <CheckCircleIcon className="h-3.5 w-3.5 shrink-0" />
            {form.address}
          </p>
        )}
      </div>

      {/* Field type pills */}
      <div>
        <label
          className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-2"
          style={{ fontFamily: HEAD_FONT }}
        >
          Field Type
        </label>
        <div className="flex flex-wrap gap-2">
          {FIELD_TYPES.map(t => (
            <button
              key={t.value}
              type="button"
              onClick={() => handleTypeChange(t.value)}
              className={clsx(
                'rounded-xl border px-3 py-1.5 text-xs font-bold uppercase transition-all',
                form.fieldType === t.value
                  ? 'border-[#94cc28] bg-[#b5f03d] text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)]'
                  : 'border-zinc-200 bg-white text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10',
              )}
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              {t.label}
            </button>
          ))}
        </div>
        {form.isIndoor && (
          <p className="mt-2 flex items-center gap-1.5 text-[11px] text-[#5a8a1f] font-medium">
            <BoltSlashIcon className="h-3.5 w-3.5" />
            Indoor venue — weather alerts are disabled for sessions here
          </p>
        )}
      </div>

      {/* Notes */}
      <div>
        <label
          className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1.5"
          style={{ fontFamily: HEAD_FONT }}
        >
          Notes <span className="font-normal normal-case tracking-normal text-zinc-400">(optional)</span>
        </label>
        <input
          type="text"
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          placeholder="e.g. Parking on west side, gate code 4821"
          className="block w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
        />
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
        >
          {saving
            ? <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Saving…</>
            : initial ? 'Save Changes' : 'Add Field'
          }
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Field card                                                           */
/* ------------------------------------------------------------------ */
function FieldCard({ field, onEdit, onDelete, deleting }) {
  return (
    <div className="flex items-start gap-4 rounded-2xl border border-zinc-200 bg-white px-5 py-4 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:shadow-[0_4px_12px_0_rgba(13,31,20,0.08)] transition-shadow">
      {/* Icon */}
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-xl">
        {fieldEmoji(field)}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-[#0d1f14]">{field.name}</p>
          {field.isIndoor && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-[#b5f03d]/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#3d6010]"
              style={{ fontFamily: HEAD_FONT }}
            >
              <BoltSlashIcon className="h-3 w-3" /> Indoor
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-zinc-500 truncate">{field.address}</p>
        {field.notes && (
          <p className="mt-0.5 text-xs text-zinc-400 italic">{field.notes}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => onEdit(field)}
          className="rounded-lg p-2 text-zinc-400 hover:text-[#5a8a1f] hover:bg-[#b5f03d]/10 transition-colors"
          title="Edit"
        >
          <PencilSquareIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(field.id)}
          disabled={deleting === field.id}
          className="rounded-lg p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          title="Delete"
        >
          {deleting === field.id
            ? <ArrowPathIcon className="h-4 w-4 animate-spin" />
            : <TrashIcon className="h-4 w-4" />
          }
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main page                                                            */
/* ------------------------------------------------------------------ */
export default function FieldsPage() {
  const { teamId }   = useParams();
  const navigate     = useNavigate();

  const [fields,   setFields]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);   // field object being edited
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);   // fieldId being deleted

  /* Live listener */
  useEffect(() => {
    const q = query(
      collection(db, 'teams', teamId, 'fields'),
      orderBy('name', 'asc'),
    );
    return onSnapshot(q, snap => {
      setFields(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
  }, [teamId]);

  /* Open add */
  const openAdd = () => { setEditing(null); setShowForm(true); };

  /* Open edit */
  const openEdit = (field) => { setEditing(field); setShowForm(true); };

  /* Cancel */
  const handleCancel = () => { setShowForm(false); setEditing(null); };

  /* Save */
  const handleSave = async (form) => {
    setSaving(true);
    try {
      const payload = {
        name:      form.name.trim(),
        address:   form.address,
        lat:       form.lat,
        lng:       form.lng,
        placeId:   form.placeId  || null,
        fieldType: form.fieldType,
        isIndoor:  form.isIndoor,
        notes:     form.notes.trim() || null,
        updatedAt: serverTimestamp(),
      };

      if (editing) {
        await updateDoc(doc(db, 'teams', teamId, 'fields', editing.id), payload);
        toast.success('Field updated.');
      } else {
        await addDoc(collection(db, 'teams', teamId, 'fields'), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        toast.success('Field added.');
      }
      handleCancel();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save field.');
    } finally {
      setSaving(false);
    }
  };

  /* Delete */
  const handleDelete = async (fieldId) => {
    if (!window.confirm('Remove this field? Past sessions will keep their saved location.')) return;
    setDeleting(fieldId);
    try {
      await deleteDoc(doc(db, 'teams', teamId, 'fields', fieldId));
      toast.success('Field removed.');
    } catch {
      toast.error('Failed to remove field.');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="mx-auto max-w-2xl p-4 sm:p-6 lg:p-8">
        {/* Team sub-nav: keeps Overview/Roster/Schedule/Chat tabs visible while drilled into team-scoped content. */}
        <TeamSubNav teamId={teamId} />

        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="mb-3 flex items-center gap-1.5 text-sm text-zinc-500 hover:text-[#5a8a1f] transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" /> Back
          </button>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1
                className="text-2xl font-bold uppercase tracking-tight text-[#0d1f14]"
                style={{ fontFamily: HEAD_FONT }}
              >
                Team Fields
              </h1>
              <Text className="mt-1">
                Saved venues for practices, games &amp; tournaments. Used to send weather alerts automatically.
              </Text>
            </div>
            {!showForm && (
              <button
                type="button"
                onClick={openAdd}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                <PlusIcon className="h-4 w-4" /> Add Field
              </button>
            )}
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <FieldForm
            initial={editing || null}
            onSave={handleSave}
            onCancel={handleCancel}
            saving={saving}
          />
        )}

        {/* List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(n => (
              <div key={n} className="h-20 rounded-2xl bg-zinc-100 animate-pulse" />
            ))}
          </div>
        ) : fields.length === 0 && !showForm ? (
          <div className="rounded-2xl border-2 border-dashed border-zinc-200 bg-white p-12 text-center shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
              <MapPinIcon className="h-6 w-6 text-[#5a8a1f]" />
            </div>
            <h3
              className="mt-4 text-base font-bold uppercase tracking-tight text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              No saved fields yet
            </h3>
            <Text className="mt-1 !text-sm">
              Add your team's regular venues to enable weather alerts and speed up session creation.
            </Text>
            <button
              type="button"
              onClick={openAdd}
              className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <PlusIcon className="h-4 w-4" /> Add Your First Field
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {fields.map(field => (
              <FieldCard
                key={field.id}
                field={field}
                onEdit={openEdit}
                onDelete={handleDelete}
                deleting={deleting}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
